import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaPlus, FaCreditCard, FaRegCreditCard, FaPiggyBank, FaIdCard, FaPassport, FaFileAlt } from 'react-icons/fa';
import { fetchSecrets, addSecret, deleteSecret } from '../store/slices/secretsSlice';
import { secretService } from '../services/secretService';
import { useRefreshData } from '../hooks/useRefreshData';
import AddSecretModal from '../components/AddSecretModal';
import ViewSecretDialog from '../components/ViewSecretDialog';
import CreditCard from '../components/CreditCard';
import DocumentCard from '../components/DocumentCard'; // Add this import

const CategoryIcons = {
  CREDIT_CARD: FaCreditCard,
  DEBIT_CARD: FaRegCreditCard,
  BANK_ACCOUNT: FaPiggyBank,
  PAN_CARD: FaIdCard,
  AADHAR_CARD: FaIdCard,
  PASSPORT: FaPassport,
  OTHER: FaFileAlt,
  DRIVING_LICENSE: FaIdCard,
};

const CategoryLabels = {
  CREDIT_CARD: 'Credit Card',
  DEBIT_CARD: 'Debit Card',
  BANK_ACCOUNT: 'Bank Account',
  PAN_CARD: 'PAN Card',
  AADHAR_CARD: 'Aadhar Card',
  PASSPORT: 'Passport',
  OTHER: 'Other',
  DRIVING_LICENSE: 'Driving License',
};

const Secrets = () => {
  const dispatch = useDispatch();
  const { items: encryptedSecrets, loading, error } = useSelector(state => state.secrets);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSecret, setSelectedSecret] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);

  // Auto-refresh data when needed
  useRefreshData(
    state => state.secrets,
    fetchSecrets
  );

  useEffect(() => {
    dispatch(fetchSecrets());
    fetchCategories();
  }, [dispatch]);

  // Update the categories fetching to use error handling and loading state
  const fetchCategories = async () => {
    try {
      const response = await secretService.getCategories();
      const formattedCategories = [
        { id: 'all', label: 'All' },
        ...response.data.map(category => ({
          id: category,
          label: CategoryLabels[category] || category,
          icon: CategoryIcons[category]
        }))
      ];
      setCategories(formattedCategories);
    } catch (err) {
      console.error('Error fetching categories:', err);
      // Optionally set an error state here
    }
  };

  const handleAddSecret = async (secretData, secretKey) => {
    try {
      const title = generateTitle(secretData.type, secretData.data);
      const payload = {
        type: secretData.type,
        category: secretData.type,
        title,
        data: secretData.data
      };
      
      await dispatch(addSecret({ payload, secretKey })).unwrap();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Failed to add secret:', err);
    }
  };

  const generateTitle = (type, data) => {
    switch (type) {
      case 'CREDIT_CARD':
      case 'DEBIT_CARD':
        return `${data.cardName} - ${data.variant}`;
      case 'BANK_ACCOUNT':
        return `${data.bankName} - ${data.accountHolderName}`;
      case 'PAN_CARD':
        return `PAN - ${data.name}`;
      case 'AADHAR_CARD':
        return `Aadhar - ${data.name}`;
      case 'PASSPORT':
        return `Passport - ${data.name}`;
      case 'OTHER':
        return data.title;
      default:
        return 'Untitled Secret';
    }
  };

  const handleDeleteSecret = async (id, secretKey) => {
    try {
      await dispatch(deleteSecret({ id, secretKey })).unwrap();
    } catch (err) {
      console.error('Failed to delete secret:', err);
    }
  };

  const handleViewSecret = async (secret, secretKey) => {
    try {
      // Implement view logic with decryption
      setSelectedSecret({ ...secret, secretKey });
      setShowViewDialog(true);
    } catch (err) {
      console.error('Failed to view secret:', err);
    }
  };

  // Filter secrets only if they exist
  const filteredSecrets = encryptedSecrets?.length 
    ? (selectedCategory === 'all' 
      ? encryptedSecrets 
      : encryptedSecrets.filter(secret => secret.category === selectedCategory))
    : [];

  const renderSecret = (secret) => {
    const documentTypes = ['AADHAR_CARD', 'PAN_CARD', 'DRIVING_LICENSE'];
    
    if (documentTypes.includes(secret.category)) {
      return (
        <DocumentCard
          key={secret._id}
          data={secret}
          onDelete={handleDeleteSecret}
        />
      );
    }
    
    if (secret.category === 'CREDIT_CARD') {
      return (
        <div key={secret._id} className="w-full flex justify-center">
          <CreditCard
            data={secret}
            onDelete={handleDeleteSecret}
            onView={handleViewSecret}
          />
        </div>
      );
    }

    // Get the icon for this category
    const Icon = CategoryIcons[secret.category] || CategoryIcons.OTHER;

    // Regular cards in 3 columns
    return (
      <div key={secret._id} className="md:col-span-2 lg:col-span-1">
        <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-2 mb-4">
            <Icon className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-800">
              {CategoryLabels[secret.category] || secret.category}
            </h3>
          </div>
          <div className="text-gray-600 mb-4">
            <div className="mb-2">
              <span className="font-medium">Category: </span>
              <span>{secret.category}</span>
            </div>
            <div className="mb-2">
              <span className="font-medium">Created: </span>
              <span>{new Date(secret.createdAt).toLocaleString()}</span>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedSecret(secret);
                setShowViewDialog(true);
              }}
              className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
              type="button"
            >
              View
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (handleDeleteSecret) {
                  handleDeleteSecret(secret._id);
                }
              }}
              disabled={loading}
              className="px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg z-10"
              type="button"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ml-16 p-6"> {/* Changed from "container mx-auto p-6" to "ml-16 p-6" */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg">
          {error}
        </div>
      )}

      <div className="max-w-7xl mx-auto"> {/* Added wrapper with max width */}
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Secrets Vault</h1>
        
        {/* Updated Categories */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map(category => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2
                  ${selectedCategory === category.id
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                type="button"
              >
                {Icon && <Icon className="w-4 h-4" />}
                {category.label}
              </button>
            );
          })}
        </div>

        {/* Add New Secret Button */}
        <button 
          onClick={() => setIsModalOpen(true)}
          disabled={loading}
          className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
          type="button"
        >
          <FaPlus className="mr-2" />
          Add New Secret
        </button>
      </div>

      {/* Updated grid layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {loading ? (
          Array(4).fill().map((_, index) => (
            <div key={index} className="h-64 bg-white rounded-lg shadow-md animate-pulse">
              <div className="h-full bg-gray-200 rounded-lg"></div>
            </div>
          ))
        ) : encryptedSecrets.length === 0 ? (
          <div className="col-span-full text-center text-gray-500 py-8">
            {loading ? 'Loading secrets...' : error ? error : 'No secrets found'}
          </div>
        ) : (
          filteredSecrets.map((secret) => renderSecret(secret))
        )}
      </div>

      {/* Keep ViewSecretDialog only for non-credit-card secrets */}
      <ViewSecretDialog
        isOpen={showViewDialog}
        onClose={() => {
          setShowViewDialog(false);
          setSelectedSecret(null);
        }}
        secret={selectedSecret}
      />

      <AddSecretModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddSecret}
        loading={loading}
      />
    </div>
  );
};

export default Secrets;
